import {
  liftLoadedState,
  loadedEndpoint,
  mapLoadedUnpack,
} from "models/loaded";
import { LoadedContent } from "../common/loading/loadedContent";
import { getBasketConstraintRows, useProductConstraintRows } from "./rows";
import {
  SearchChemistryGroups,
  SearchProductGroups,
  SearchChemicalElements,
  SearchSteelGrades,
} from "contexts/search/context";
import { alpha, Box, LinearProgress, Stack, Typography } from "@mui/material";
import {
  useBasketConstraintColumns,
  useProductConstraintColumns,
} from "./columns";
import { HotkeyCompliantDataGrid } from "src/components/common/dataGrid";
import { AccountPageLayout } from "../common/AccountPageLayout";
import { useTenantTranslation } from "hooks/formatters";
import {
  FormattedBasketConstraint,
  useGetFormattedBasketConstraintsQuery,
} from "src/store/api/generatedApi";
import { useTenant } from "hooks/settings";
import {
  BackToCurrentConstraintsButton,
  SelectSearchButton,
} from "./selectSearch";
import { useState } from "react";
import {
  useChemicalElements,
  useChemistryGroups,
  useProductGroups,
  useSteelGrades,
  useSearch,
  useMaterialsId,
  useMaterialPhysicsId,
} from "contexts/search/SearchContextProvider";
import { useLoadMostRecentSearch } from "contexts/search/effects";
import { TitledTabs } from "../common/titledTabs";
import React from "react";
import { MultiSelect } from "../common/inputs/multiSelect";
import { useMaterialLimits } from "hooks/search";

export const ConstraintsPage = () => {
  const mostRecentSearch = useLoadMostRecentSearch();
  const {
    parameters: { client },
  } = useSearch();
  const { t } = useTenantTranslation();

  const name =
    mapLoadedUnpack(client, ({ session_name }) =>
      mostRecentSearch.currentlyLoaded ? null : session_name
    ) ?? t("currentConstraints");

  return (
    <AccountPageLayout title={t("constraints")}>
      <Stack sx={{ flex: 1, overflow: "hidden" }}>
        <Stack direction="row" alignItems="center" gap={2}>
          <Typography>{`${t("currentlyViewing")}: ${name}`}</Typography>
          <Box>
            {mostRecentSearch.currentlyLoaded ? (
              <SelectSearchButton />
            ) : (
              <BackToCurrentConstraintsButton {...mostRecentSearch} />
            )}
          </Box>
        </Stack>
        <Stack sx={{ flex: 1, overflow: "hidden" }}>
          <ConstraintsPageContent />
        </Stack>
      </Stack>
    </AccountPageLayout>
  );
};

export const ConstraintsPageContent = () => {
  return (
    <LoadedContent
      enableFullHeight
      data={liftLoadedState({
        chemistryGroups: useChemistryGroups(),
        productGroups: useProductGroups(),
        chemicalElements: useChemicalElements(),
        steelGrades: useSteelGrades(),
        materialsId: useMaterialsId(),
        materialPhysicsId: useMaterialPhysicsId(),
        basketConstraints: loadedEndpoint(
          useGetFormattedBasketConstraintsQuery({ tenantName: useTenant() })
        ),
      })}
      loading={<LinearProgress />}
      waiting={<LinearProgress />}
      disableFetching
    >
      {(lookups) => <LoadedConstraintsPage {...lookups} />}
    </LoadedContent>
  );
};

const LoadedConstraintsPage = ({
  chemistryGroups,
  productGroups,
  chemicalElements,
  steelGrades,
  basketConstraints,
}: {
  chemistryGroups: SearchChemistryGroups;
  productGroups: SearchProductGroups | null;
  chemicalElements: SearchChemicalElements;
  steelGrades: SearchSteelGrades;
  basketConstraints: FormattedBasketConstraint[];
}) => {
  const { t } = useTenantTranslation();

  const [filterSteelGrades, setFilterSteelGrades] = useState<string[]>([]);
  const { materialLimits } = useMaterialLimits();

  const productConstraintRows = useProductConstraintRows(
    chemistryGroups,
    productGroups,
    chemicalElements,
    steelGrades,
    materialLimits.status === "success" ? materialLimits.data : [],
    filterSteelGrades
  );
  const productConstraintColumns = useProductConstraintColumns(
    productConstraintRows
  );

  const basketConstraintRows = getBasketConstraintRows(basketConstraints);
  const basketConstraintColumns = useBasketConstraintColumns();

  const [selectedTabKey, setSelectedTabKey] = React.useState("products");

  return (
    <TitledTabs
      selectedTabKey={selectedTabKey}
      onChange={setSelectedTabKey}
      tabSpecs={[
        {
          title: t("products"),
          key: "products",
          content: (
            <Stack
              sx={{
                maxHeight: "100%",
                alignItems: "stretch",
                gap: 1,
              }}
            >
              <MultiSelect
                sx={{ width: 500, alignSelf: "flex-end", paddingTop: 2 }}
                options={steelGrades.byIndex.map((grade) => grade.name)}
                getOptionId={(option) => option}
                getOptionLabel={(option) => option}
                values={filterSteelGrades}
                setValues={setFilterSteelGrades}
                label={t("searchForSteelGrade")}
                small
              />
              <HotkeyCompliantDataGrid
                getRowHeight={() => "auto"}
                sx={{
                  minHeight: 200,
                  "& .matches-filter": {
                    backgroundColor: (theme) =>
                      alpha(theme.palette.info.main, 0.2),
                  },
                }}
                rows={productConstraintRows}
                columns={productConstraintColumns}
              />
            </Stack>
          ),
        },
        {
          title: t("baskets"),
          key: "baskets",
          content: (
            <Stack sx={{ maxHeight: "100%" }}>
              <HotkeyCompliantDataGrid
                sx={{ marginTop: 2, flex: 1 }}
                rows={basketConstraintRows}
                columns={basketConstraintColumns}
              />
            </Stack>
          ),
        },
      ]}
    />
  );
};
